import { ChevronRight } from "lucide-react";
import { Avatar } from "@/components/Avatar";
import { cn } from "@/lib/cn";
import { formatBRL } from "./prosthesis-meta";

export type DentistaDosPedidos = {
  userId: string | null;
  nome: string;
  avatarUrl: string | null;
  emAberto: number;
  atrasados: number;
  noPeriodo: number;
  concluidos: number;
  valorNoPeriodoCents: number | null;
  laboratorios: string[];
};

/** Cartão de um dentista na aba "Por dentista" — tocar abre os pedidos dele. */
export function CartaoDoDentista({
  dentista,
  mostraValor,
  onAbrir,
}: {
  dentista: DentistaDosPedidos;
  mostraValor: boolean;
  onAbrir: () => void;
}) {
  const semDentista = !dentista.userId;
  return (
    <button
      type="button"
      onClick={onAbrir}
      className={cn(
        "group flex flex-col gap-3 rounded-xl border bg-white p-4 text-left transition-colors hover:border-brand-300 dark:bg-card dark:hover:border-primary/50",
        semDentista ? "border-dashed border-zinc-200 dark:border-border" : "border-zinc-200 dark:border-border",
      )}
    >
      <div className="flex w-full items-center gap-2.5">
        <Avatar src={dentista.avatarUrl ?? undefined} name={semDentista ? "?" : dentista.nome} size="sm" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-zinc-900 dark:text-foreground">{dentista.nome}</p>
          <p className="truncate text-[11px] text-zinc-500 dark:text-muted-foreground">
            {semDentista
              ? "Pedidos antigos sem autor registrado"
              : dentista.laboratorios.length
                ? dentista.laboratorios.join(" · ")
                : "Sem laboratório definido"}
          </p>
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-zinc-300 transition-colors group-hover:text-brand-600 dark:text-muted-foreground" />
      </div>

      <div className="grid w-full grid-cols-3 gap-2 border-t border-zinc-100 pt-3 dark:border-border">
        <Numero rotulo="Em aberto" valor={dentista.emAberto} />
        <Numero rotulo="Atrasados" valor={dentista.atrasados} alerta={dentista.atrasados > 0} />
        <Numero rotulo="No período" valor={dentista.noPeriodo} />
      </div>

      {mostraValor && (
        <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">
          Valor no período{" "}
          <strong className="font-semibold tabular-nums text-zinc-900 dark:text-foreground">
            {formatBRL(dentista.valorNoPeriodoCents ?? 0)}
          </strong>
        </p>
      )}
    </button>
  );
}

function Numero({ rotulo, valor, alerta }: { rotulo: string; valor: number; alerta?: boolean }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-muted-foreground/80">
        {rotulo}
      </p>
      <p
        className={cn(
          "text-lg font-semibold tabular-nums",
          alerta ? "text-amber-600 dark:text-amber-400" : "text-zinc-900 dark:text-foreground",
        )}
      >
        {valor}
      </p>
    </div>
  );
}
